import { useEffect, useState } from 'react';

export interface LaunchCountdown {
  hours: string;
  minutes: string;
  seconds: string;
  isExpired: boolean;
}

const COUNTDOWN_MINUTES = 10;
const STORAGE_KEY = 'zumba-launch-deadline';

/**
 * The launch window lasts 10 minutes per visitor and is stored locally, so
 * refreshing or navigating back does not restart it.
 */
function getDeadline(): number {
  const fallback = Date.now() + COUNTDOWN_MINUTES * 60 * 1000;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? Number(stored) : Number.NaN;

    if (Number.isFinite(parsed)) return parsed;

    window.localStorage.setItem(STORAGE_KEY, String(fallback));
    return fallback;
  } catch {
    return fallback;
  }
}

function getRemaining(deadline: number): number {
  return Math.max(0, deadline - Date.now());
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Lightweight countdown with no dependencies. It derives the remaining time
 * from the clock on every tick, so backgrounding the tab or sleeping the
 * device never desynchronises it.
 */
export function useLaunchCountdown(): LaunchCountdown {
  const [remaining, setRemaining] = useState<number>(() => getRemaining(getDeadline()));

  useEffect(() => {
    const deadline = getDeadline();
    let timer: number | undefined;

    const clear = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };

    const tick = () => {
      clear();
      const next = getRemaining(deadline);
      setRemaining(next);
      if (next <= 0) return;
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
    };

    const handleVisibility = () => {
      if (document.hidden) {
        clear();
        return;
      }
      tick();
    };

    tick();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clear();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const totalSeconds = Math.floor(remaining / 1000);

  return {
    hours: pad(Math.floor(totalSeconds / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
    isExpired: remaining <= 0,
  };
}
